/** Port interfaces — domain depends on these; infrastructure implements them. */

import type { User } from './user';
import type {
  SignUpProps,
  SocialProfile,
  SocialProvider,
  ClientAppUserResult,
  ServiceSignInResult,
  ServiceSignUpResult,
  ServiceForgotPasswordResult,
  ServiceValidationResult,
  ValidateUserRequest,
  AuthProviderFeatures,
} from './results';
import type { CognitoCode } from './cognito-code';

// ─── Infrastructure ports (unchanged — consumed by service implementations) ──

export interface AuthService {
  readonly supportedFeatures: AuthProviderFeatures;
  checkClientAppUser(email: string): Promise<ClientAppUserResult>;
  checkCognitoUser(email: string): Promise<CognitoCode>;
  signIn(email: string, password: string): Promise<ServiceSignInResult>;
  signUp(props: SignUpProps): Promise<ServiceSignUpResult>;
  confirmSignUp(email: string, code: string, autoSignIn?: { password: string; phone?: string; firstName: string; lastName: string }): Promise<ServiceSignUpResult>;
  resendSignUpCode(email: string): Promise<void>;
  signOut(redirectUrl?: string): Promise<void>;
  validateUser(request: ValidateUserRequest): Promise<ServiceValidationResult>;
  /** Initiates an OAuth redirect to the identity provider (Cognito path). */
  idpRedirectSignIn(provider: SocialProvider, customState?: string): Promise<void>;
  authenticateAfterRedirect(provider: SocialProvider): Promise<ServiceSignInResult>;
  /**
   * @deprecated Legacy client-app path. Posts the SDK-obtained token directly to the Rails API.
   * Use `idpRedirectSignIn` for new social sign-in flows.
   */
  legacySocialSignIn(profile: SocialProfile): Promise<ServiceSignInResult>;
  forgotPassword(email: string, theme: string): Promise<ServiceForgotPasswordResult>;
  confirmForgotPassword(email: string, code: string, password: string, theme: string): Promise<ServiceForgotPasswordResult>;
  changePassword(currentPassword: string, newPassword: string): Promise<ServiceForgotPasswordResult>;
  confirmUser(token: string, theme: string): Promise<ServiceSignInResult>;
  refreshSession(): Promise<ServiceSignInResult | null>;
  submitPhone(userId: string | number, phone: string, profile?: { firstName: string; lastName: string }): Promise<ServiceSignInResult | void>;
  getSignInProvider(): Promise<string | null>;
  getLogoutUrl(redirectUri: string): string | null;
  deleteAccount(): Promise<void>;
  clearStaleSession(): Promise<void>;
  /** Cognito-first profile update (name, phone). Cognito updated first; Rails updated on success. */
  updateProfile(params: User): Promise<{ success: true } | { error: string }>;
  /** Initiates a Cognito email change and updates Rails. User must confirm with confirmEmailUpdate(). */
  updateEmail(currentUser: User, newEmail: string, retypeEmail: string, password?: string, passwordConfirmation?: string): Promise<{ success: true } | { error: string }>;
  /** Commits the pending Cognito email change with the verification code sent to the new address. */
  confirmEmailUpdate(code: string): Promise<{ success: true } | { error: string }>;
  /** Registers a one-shot listener for OAuth redirect completion (Cognito only). */
  onOAuthRedirectComplete?(onSuccess: () => void, onError: (error: string) => void): () => void;
}

// ─── Persistence (cookie-backed user + JWT storage) ─────────────────────────

export type PersistenceCommand =
  | { type: 'SAVE_USER'; user: User }
  | { type: 'SAVE_JWT'; jwt: string }
  | { type: 'CLEAR' };

export interface SessionPersistence {
  dispatch(command: PersistenceCommand): void;
  loadUser(): User | null;
}

// ─── Session store (cross-redirect state in sessionStorage) ─────────────────

export type SessionStoreCommand =
  | { type: 'SET_PENDING_ERROR'; error: { code: string; message: string } }
  | { type: 'CLEAR_PENDING_ERROR' }
  | { type: 'SET_NEEDS_PHONE' }
  | { type: 'CLEAR_NEEDS_PHONE' }
  | { type: 'SET_SOCIAL_PENDING'; state: { provider: string; returnTo: string; phase: string } }
  | { type: 'CLEAR_SOCIAL_PENDING' }
  | { type: 'SET_SOCIAL_PROFILE'; profile: { firstName: string; lastName: string } }
  | { type: 'CLEAR_SOCIAL_PROFILE' }
  | { type: 'SET_PENDING_AUTH'; user: User }
  | { type: 'CLEAR_PENDING_AUTH' }
  | { type: 'CLEAR_ALL' };

export interface AuthSessionStore {
  dispatch(command: SessionStoreCommand): void;
  getPendingError(): { code?: string; message: string } | null;
  getNeedsPhone(): boolean;
  getSocialPending(): { provider: string; returnTo: string; phase: string } | null;
  getSocialProfile(): { firstName: string; lastName: string } | null;
  getPendingAuth(): User | null;
}

// ─── Translation ────────────────────────────────────────────────────────────

export interface TranslationPort {
  t(key: string, opts?: { defaultValue: string }): string;
}

// ─── Observability (analytics / error tracking) ─────────────────────────────

export type ObservabilityCommand =
  | { type: 'signin_failure'; error: unknown; authMethod?: string }
  | { type: 'signin_success'; authMethod?: string }
  | { type: 'register_failure'; error: unknown; authMethod?: string }
  | { type: 'register_success'; authMethod?: string }
  | { type: 'social_signin_success'; provider: string }
  | { type: 'social_signin_failure'; error: unknown; provider: string }
  | { type: 'change_password_success' }
  | { type: 'change_password_failure'; error: unknown }
  | { type: 'submit_phone_success' }
  | { type: 'submit_phone_failure'; error: unknown }
  | { type: 'session_refresh_success' }
  | { type: 'session_failure'; error: unknown }
  | { type: 'delete_account_success' }
  | { type: 'delete_account_failure'; error: unknown }
  | { type: 'email_lookup_failure'; error: unknown }
  | { type: 'idp_probe_failure'; error: unknown }
  | { type: 'signout_failure'; error: unknown }
  | { type: 'resend_code_failure'; error: unknown; email: string }
  | { type: 'clear_stale_session_failure'; error: unknown }
  | { type: 'bootstrap_failure'; error: unknown }
  | { type: 'update_profile_failure'; error: unknown }
  | { type: 'update_email_failure'; error: unknown }
  | { type: 'update_email_confirmation_failure'; error: unknown };

export interface AuthObservabilityService {
  dispatch(command: ObservabilityCommand): void;
}

export const NOOP_OBSERVABILITY: AuthObservabilityService = {
  dispatch: () => {},
};

// ─── Amplify Hub bridge (consumed by infrastructure/amplify/config.ts) ───────

/**
 * Implemented by `AuthSession`. Passed to `configureAmplifyAuth` so the Amplify
 * Hub can call back into the domain layer when social sign-in events fire.
 */
export interface SocialAuthListener {
  legacySdkSignInComplete(profile: SocialProfile): void;
  failSocialAuth(error: string): void;
  cognitoSocialSignInComplete(): void;
  setSocialRedirectState(encodedState: string): void;
}

// ─── Shared types ─────────────────────────────────────────────────────────────

export type PendingAction =
  | { type: 'collect_phone' }
  | { type: 'error'; message: string; code?: string };

export interface AuthSessionSnapshot {
  user: User | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

export interface AuthSessionDeps {
  authService: AuthService;
  persistence: SessionPersistence;
  sessionStore: AuthSessionStore;
  ssrUser: User | null;
  debug?: boolean;
  restoreReturnTo?: (target: string) => void;
  observability?: AuthObservabilityService;
}

// ─── Role interfaces (AuthSession implements all five) ────────────────────────

export interface SessionLifecycle {
  readonly supportedFeatures: AuthProviderFeatures;
  subscribe(listener: () => void): () => void;
  getSnapshot(): AuthSessionSnapshot;
  getServerSnapshot(): AuthSessionSnapshot;
  completeAuth(user: User): void;
  signOut(redirectUrl?: string): Promise<void>;
  setUser(user: User): void;
  refreshSession(): Promise<void>;
  getSignInProvider(): Promise<string | null>;
  getLogoutUrl(redirectUri: string): string | null;
  destroy(): void;
}

export interface SocialAuth {
  /**
   * Initiates social sign-in for the given provider.
   * Internally routes to the Cognito OAuth redirect or the legacy SDK token exchange
   * depending on the active auth provider — callers do not need to know which.
   */
  signInWithSocial(provider: SocialProvider): void;
  /**
   * @deprecated Legacy client-app path only. Posts an SDK-obtained token to the Rails API.
   * Called externally by the social SDK callback after it completes its own OAuth flow.
   * Not needed when Cognito is active.
   */
  legacySocialSignIn(profile: SocialProfile): Promise<void>;
  hasPendingAfterLogout(): boolean;
  resumeAfterLogout(): void;
  failSocialAuth(error: string): void;
}

export interface AccountSettings {
  submitPhone(phone: string, profile?: { firstName: string; lastName: string }): Promise<{ ok: true } | { ok: false; error: string }>;
  deleteAccount(): Promise<{ ok: true } | { ok: false; error: string }>;
  changePassword(currentPassword: string, newPassword: string): Promise<{ ok: true } | { ok: false; error: string }>;
  /** Cognito-first profile update (name, phone). Cognito updated first; Rails updated on success. */
  updateProfile(params: User): Promise<{ ok: true } | { ok: false; error: string }>;
  /** Initiates a Cognito email change and updates Rails. User must confirm with confirmEmailUpdate(). */
  updateEmail(currentUser: User, newEmail: string, retypeEmail: string, password?: string, passwordConfirmation?: string): Promise<{ ok: true } | { ok: false; error: string }>;
  /** Commits the pending Cognito email change with the verification code sent to the new address. */
  confirmEmailUpdate(code: string): Promise<{ ok: true } | { ok: false; error: string }>;
}

export interface PendingActions {
  consumePendingAction(): PendingAction | null;
  peekPendingAction(): PendingAction | null;
  clearPendingAction(): void;
}

export interface UserContextAuth {
  signIn(email: string, password: string): Promise<ServiceSignInResult>;
  signUp(props: SignUpProps): Promise<ServiceSignUpResult>;
  confirmSignUp(email: string, code: string): Promise<ServiceSignUpResult>;
  resendSignUpCode(email: string): Promise<void>;
  validateUser(request: ValidateUserRequest): Promise<ServiceValidationResult>;
  forgotPassword(email: string, theme: string): Promise<ServiceForgotPasswordResult>;
  confirmForgotPassword(email: string, code: string, password: string, theme: string): Promise<ServiceForgotPasswordResult>;
  completeAuth(user: User): void;
}

/** Full facade — AuthSession implements all five role interfaces. */
export type AuthSessionPort =
  SessionLifecycle & SocialAuth & AccountSettings & PendingActions & UserContextAuth;

